/**
 * Copyright (c) 2025 Lauft App Inc. All rights reserved.
 *
 * ConditionByMetro.js — TEMPORARY internal tool (admin dash).
 *
 * Auto-assign model condition (conditionAnalysis) to ACTIVE listings in a
 * chosen metro, imported within the last N days. Reuses each property's
 * existing per-room images (no re-scan). Background job + email; addresses
 * log live to Render. Delete when done.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { MapPin, AlertCircle, Loader, Play } from 'react-feather';
import AuthService from '../../services/authService';

const API_URL = 'https://back-end-x7tx.onrender.com/api';

const C = {
  bg: '#fcfcfc', panel: '#eeebe3', accent: '#fdd700', ink: '#1a1a1a',
  sage: '#7a9471', border: '#e2ddd2', muted: '#8a8578'
};

const ConditionByMetro = () => {
  const [metros, setMetros] = useState([]);
  const [metro, setMetro] = useState('');
  const [daysBack, setDaysBack] = useState(30);
  const [count, setCount] = useState(200);
  const [running, setRunning] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  const loadMetros = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/admin/labeling/metros`, {
        headers: { Authorization: `Bearer ${AuthService.getToken()}` }
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Failed to load metros');
      setMetros(json.data);
      if (json.data.length) setMetro(json.data[0]);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => { loadMetros(); }, [loadMetros]);

  const run = async () => {
    setRunning(true);
    setMsg('');
    setError('');
    try {
      const res = await fetch(`${API_URL}/admin/labeling/auto-assign-metro`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({ metro, daysBack: Number(daysBack), count: Number(count) })
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Failed to start');
      setMsg(
        json.scored === 0
          ? (json.message || 'Nothing to score.')
          : `Started on ${json.batchSize} listings (${json.totalRemaining} match remain). Watch Render logs / email report when done.`
      );
    } catch (e) {
      setError(e.message);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div style={S.wrap}>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0, color: C.ink }}>Condition by Metro</h2>
        <div style={{ color: C.muted, fontSize: 13, marginTop: 4 }}>
          Assign model condition to active listings in a metro, imported in the last N days.
          Runs in the background; addresses log to Render, summary emailed.
        </div>
      </div>

      {error && <div style={S.errorBar}><AlertCircle size={16} /> {error}</div>}

      <div style={S.card}>
        <div style={S.field}>
          <label style={S.label}><MapPin size={14} /> Metro</label>
          <select style={S.select} value={metro} onChange={(e) => setMetro(e.target.value)}>
            {metros.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>

        <div style={S.field}>
          <label style={S.label}>Imported within (days)</label>
          <input
            type="number" min={1} max={365} value={daysBack}
            onChange={(e) => setDaysBack(e.target.value)} style={S.input}
          />
        </div>

        <div style={S.field}>
          <label style={S.label}>Max to score (≤1000)</label>
          <input
            type="number" min={1} max={1000} value={count}
            onChange={(e) => setCount(e.target.value)} style={S.input}
          />
        </div>

        <button style={running ? S.btnBusy : S.btn} onClick={run} disabled={running || !metro}>
          {running ? <><Loader size={15} className="spin" /> Starting…</> : <><Play size={15} /> Run</>}
        </button>

        {msg && <div style={S.msg}>{msg}</div>}
      </div>

      <style>{`.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

const S = {
  wrap: { padding: 24, background: C.bg, minHeight: '100%', fontFamily: 'system-ui, sans-serif' },
  errorBar: { display: 'flex', alignItems: 'center', gap: 8, background: '#fdecea', color: '#b71c1c', border: '1px solid #f5c6cb', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 14 },
  card: { background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, maxWidth: 460, display: 'flex', flexDirection: 'column', gap: 16 },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: C.ink },
  select: { padding: '10px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, color: C.ink, background: '#fff' },
  input: { padding: '10px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, color: C.ink, width: 140 },
  btn: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: C.accent, color: C.ink, border: 'none', borderRadius: 8, padding: '12px 18px', fontWeight: 700, cursor: 'pointer', fontSize: 15 },
  btnBusy: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: C.panel, color: C.muted, border: 'none', borderRadius: 8, padding: '12px 18px', fontWeight: 700, cursor: 'wait', fontSize: 15 },
  msg: { color: C.sage, fontSize: 14, lineHeight: 1.4 }
};

export default ConditionByMetro;